/**
 * `R3-UI0` · **EL IDENTIFICADOR ACORTADO CON SU BURBUJA Y «Copiar» — UNA VEZ.**
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ POR QUÉ ESTO VIVE EN `ui-marco` Y NO ADENTRO DE LA PRIMERA PANTALLA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R3.md:717` (R3-F1, Conversaciones) y `PRD_R3.md:1973` (R3-F5,
 * Participantes) piden **el mismo patrón**, y cada uno lo dice apuntando al otro:
 *
 *   · `:717` — *«Es el mismo comportamiento que tiene el ID de Participantes
 *     (R3-F5).»*
 *   · `:1973` — *«Es el mismo comportamiento que tienen los identificadores de
 *     Conversaciones (R3-F1).»*
 *
 * ⛔ Dos frases que se señalan mutuamente no se pueden cumplir con dos copias:
 * la primera vez que alguien ajuste el cierre con Escape, o el foco, o el texto
 * del botón, las dos divergen y **ningún criterio de ninguna de las dos
 * pantallas lo mide** —cada una está verde contra su propia copia—. Es la especie
 * que `paginado.tsx:1-30` ya pagó con ocho copias de la barra de paginado, y la
 * que `zonas.ts` §`derivaAHumano` pagó con dos condicionales de polaridad
 * opuesta. Acá nace UNA definición, antes de que exista el primer consumidor.
 *
 * ⇒ lo consumen `R3-UI1` (Conversación, Sesión, Participante) y `R3-UI3` (la
 * columna ID de Participantes). **Esta etapa no integra ninguna pantalla**: acá
 * se afirma la primitiva. Sus criterios de pantalla —`R3-F1-CA-23`, `CA-24`,
 * `CA-25` y `R3-F5-CA-10`— son de esas etapas.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ EL RECORTE: CUATRO CARACTERES, Y OCHO O MENOS NO SE RECORTA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `PRD_R3.md:717`, textual: *«se muestran con sus primeros cuatro caracteres
 * seguidos de “…” (por ejemplo, `ca010000-0000-4000-8000-000000003101` se ve
 * “ca01…”); un identificador de ocho caracteres o menos se muestra completo»*.
 *
 * ⚠️ Los dos números son distintos a propósito y **no son el mismo umbral**: se
 * muestran 4, pero el recorte recién se activa pasando de 8. Un identificador de
 * 6 caracteres se ve entero: recortarlo a 4 + «…» son 5 caracteres en pantalla
 * para ahorrar uno, y encima esconde información.
 *
 * ⭐ El «…» es **U+2026 HORIZONTAL ELLIPSIS**, un carácter, no tres puntos. Es el
 * mismo que ya usa `conversaciones/fila-desplegable.tsx` para el mensaje
 * recortado: tres puntos sueltos se leen distinto y no se pueden buscar igual.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ SÓLO LO ACORTADO ABRE BURBUJA, Y ESO ES LO QUE EL PRD DICE
 * ════════════════════════════════════════════════════════════════════════════
 *
 * *«Al pasar el cursor, tocar **un identificador acortado** o llegar a él con el
 * teclado se abre una burbuja»*. Un identificador que se muestra completo **no
 * tiene nada que revelar**: darle burbuja igual sería ofrecer un control que no
 * agrega un carácter, y además dejaría un `<button>` de más en cada fila de una
 * tabla que `R3-F1` declara de solo lectura.
 *
 * ⇒ sin recorte, esto renderiza **texto y nada más**: ni botón, ni burbuja, ni
 * manejador. El caso negativo del examen mide justamente eso.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ TRES APERTURAS Y TRES CIERRES, CADA UNO CON SU MECANISMO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Abre: **cursor encima** (`onMouseEnter` del envoltorio), **toque**
 * (`onClick` del disparador) y **teclado** (`onFocus` del disparador).
 * Cierra: **Escape**, **tocar fuera** y **alejar el cursor**.
 *
 * ⛔⛔ **LOS TRES QUE ABREN, ABREN. NINGUNO ALTERNA — y eso es una reparación de
 * `[25/9/2026]`, no un detalle de estilo.** `onClick` era
 * ~~`setAbierta((estaba) => !estaba)`~~, y un toque real despacha `focus` **antes**
 * del `click`: `focus` abría y `click` cerraba, **en el mismo gesto del dedo**. El
 * PRD enumera **tres** cierres y el segundo toque sobre el disparador no es ninguno
 * de los tres. Ver §`onClick` en el cuerpo, y el caso
 * «el TOQUE REAL (`focus` → `click`)» de `pruebas/r3-ui0-primitivas.test.tsx`, que
 * es el único que encadena la secuencia del navegador: el helper `apretar` del arnés
 * despacha `click` **suelto**, que es una secuencia que ningún navegador produce.
 *
 * ⛔ **Escape y «tocar fuera» escuchan en `document`, no en el envoltorio**, por
 * la misma razón que `banco-de-contenido/dialogo.tsx:108-113` ya dejó escrita: el
 * foco puede haberse ido a cualquier parte, y un `onKeyDown` en el envoltorio no
 * ve la tecla si el foco no está adentro. «Tocar fuera» se decide preguntándole
 * al envoltorio si CONTIENE el destino del toque; sin ese `contains`, el mismo
 * toque que abre la burbuja la cierra en el mismo turno.
 *
 * ⛔ **`pointerdown` y no `click` para el toque de afuera.** Con `click`, apretar
 * «Copiar» adentro de la burbuja llega al documento DESPUÉS de que React ya
 * procesó el botón, pero un toque que empieza afuera y termina adentro (arrastre)
 * deja la burbuja abierta. `pointerdown` cierra en el mismo gesto en que el
 * operador decide irse, que es lo que «al tocar fuera» describe.
 *
 * ⚠️ **El cierre por cursor es del ENVOLTORIO y la burbuja vive adentro de él.**
 * Si la burbuja fuera hermana, mover el cursor del identificador a «Copiar»
 * dispararía `mouseleave` y cerraría la burbuja antes de poder apretar el botón
 * — el defecto clásico de los tooltips con acciones adentro.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️ QUÉ NO HACE, DICHO ACÁ Y NO DESCUBIERTO DESPUÉS
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   · **No dice «Copiado».** El PRD pide un botón «Copiar» *«que lo copia al
 *     portapapeles»* y nada más. Un texto de confirmación sería copy que ningún
 *     documento fija, y esta consola tiene tres voces para el texto de pantalla
 *     (PRD, sistema visual, boceto): el lugar de una confirmación es el PRD, no
 *     este archivo. La burbuja **queda abierta** después de copiar, que es lo que
 *     le permite al operador verificar lo que se llevó.
 *   · **No informa un portapapeles que falla.** `navigator.clipboard.writeText`
 *     rechaza sin permiso o fuera de un contexto seguro. El rechazo se atrapa
 *     —una promesa rechazada sin manejar tumba la pantalla en desarrollo— y no se
 *     dibuja nada, porque no hay texto declarado para eso. Queda como lo único
 *     que este archivo hace en silencio.
 *   · **No posiciona la burbuja.** Eso es de `estilos.css` §«Patrones de R3», y
 *     `happy-dom` no hace layout: ningún examen de `pruebas/` puede afirmar dónde
 *     cae. Se cierra mirando la pantalla construida.
 */

import { useEffect, useRef, useState } from "react"
import type { JSX } from "react"

/** Cuántos caracteres del identificador se muestran al recortarlo. `PRD_R3.md:717`. */
export const CARACTERES_DEL_IDENTIFICADOR = 4

/** Hasta este largo el identificador se muestra completo. `PRD_R3.md:717`. */
export const LARGO_QUE_NO_SE_ACORTA = 8

/**
 * El identificador tal como se ve en la celda.
 *
 * Se exporta aparte del componente porque el CSV **no** se acorta
 * (`PRD_R3.md` regla 29: `id_conversacion` va completo) y porque un examen que
 * quiera afirmar el recorte sin montar React puede llamarla directo.
 */
export function acortarIdentificador(identificador: string): string {
  return identificador.length <= LARGO_QUE_NO_SE_ACORTA
    ? identificador
    : `${identificador.slice(0, CARACTERES_DEL_IDENTIFICADOR)}…`
}

export function IdentificadorAcortado(props: {
  /** El identificador COMPLETO. La primitiva dibuja el acortado. */
  readonly identificador: string
  /**
   * Qué identificador es, para el nombre accesible del disparador: «Conversación
   * ca010000-…». Sin esto, una fila con tres identificadores acortados le
   * ofrecería a un lector de pantalla tres botones llamados «ca01…», «5e3f…» y
   * «35a1…», y ninguno diría de qué son.
   *
   * ⚠️ Es la misma lección que `paginado.tsx` §`etiqueta` y que el `aria-label`
   * de «Filas por página» de `configuracion-del-chatbot/auditoria.tsx:453`: un
   * nombre accesible que sale del `textContent` de un envoltorio no es un nombre.
   */
  readonly que: string
}): JSX.Element {
  const { identificador, que } = props
  const acortado = acortarIdentificador(identificador)
  const estaAcortado = acortado !== identificador

  const envoltorio = useRef<HTMLSpanElement | null>(null)
  const [abierta, setAbierta] = useState(false)

  /*
   * ⛔ Escape y «tocar fuera», en `document`. Los dos se registran SÓLO con la
   * burbuja abierta: dos oyentes de documento por cada identificador de cada fila
   * de una tabla de 500 filas es el tipo de costo que no se ve hasta que la
   * pantalla se traba.
   */
  useEffect(() => {
    if (!abierta) return
    const alPresionar = (evento: KeyboardEvent): void => {
      if (evento.key === "Escape") setAbierta(false)
    }
    const alTocar = (evento: Event): void => {
      const destino = evento.target
      if (destino instanceof Node && envoltorio.current?.contains(destino) === true) return
      setAbierta(false)
    }
    document.addEventListener("keydown", alPresionar)
    document.addEventListener("pointerdown", alTocar)
    return () => {
      document.removeEventListener("keydown", alPresionar)
      document.removeEventListener("pointerdown", alTocar)
    }
  }, [abierta])

  if (!estaAcortado) {
    /*
     * ⛔ Completo ⇒ texto y nada más. Lleva la clase igual para que la celda no
     * cambie de tipografía según el largo del dato que le tocó.
     */
    return <span className="identificador-acortado">{identificador}</span>
  }

  const copiar = (): void => {
    /*
     * ⚠️ `void` + `catch` vacío: el rechazo se atrapa a propósito (ver el
     * docblock §«Qué no hace»). Sin el `catch`, un portapapeles negado deja una
     * promesa rechazada sin manejar.
     */
    void navigator.clipboard?.writeText(identificador).catch(() => undefined)
  }

  return (
    <span
      className="identificador-acortado"
      ref={envoltorio}
      onMouseEnter={() => setAbierta(true)}
      onMouseLeave={() => setAbierta(false)}
    >
      <button
        type="button"
        className="identificador-acortado__disparador"
        aria-expanded={abierta}
        aria-label={`${que} ${identificador}`}
        /*
         * ⛔⛔ `R3-UI0` `[25/9/2026]` · **EL TOQUE ABRE. NO ALTERNA.** Acá decía
         * ~~`onClick={() => setAbierta((estaba) => !estaba)}`~~, y con el dedo eso
         * **no cumplía** `PRD_R3.md:717` (*«**tocar** un identificador acortado …
         * se abre una burbuja»*).
         *
         * ⭐ **La razón es la secuencia que despacha un navegador real**, no una
         * preferencia: un toque produce `pointerdown → (mouseover/mouseenter
         * emulados) → mousedown → **focus** → mouseup → **click**`. `mouseenter` y
         * `focus` la abren, y el `click` que llega al final la **alternaba a
         * cerrada** ⇒ el dedo abría y cerraba la burbuja en el mismo gesto.
         *
         * ⛔ **Y ningún examen de `pruebas/` podía verlo**: el helper `apretar`
         * (`pruebas/entorno.ts:95`) despacha **sólo** `new MouseEvent("click")`,
         * una secuencia que ningún navegador produce. El caso que lo mide ahora
         * encadena `focusin → click` a mano
         * (`pruebas/r3-ui0-primitivas.test.tsx`, §«el TOQUE REAL»).
         *
         * ⚠️ **El PRD no pide que un segundo toque la cierre**, y los tres cierres
         * que sí pide —Escape, tocar fuera, alejar el cursor— están todos arriba.
         * Un cierre por alternancia del propio disparador sería un cuarto cierre
         * que ningún documento fija, y es el que rompe el toque.
         */
        onClick={() => setAbierta(true)}
        onFocus={() => setAbierta(true)}
      >
        {acortado}
      </button>

      {abierta && (
        <span className="identificador-acortado__burbuja" role="group" aria-label={que}>
          {/*
            `<code>` y no un `<span>`: es un identificador opaco. Un UUID en
            tipografía proporcional confunde `l` con `1` y `0` con `O`, que es
            exactamente lo que alguien que lo está por pegar en una consulta no
            puede permitirse.

            ⛔ **MEDIDO, y por eso la fuente se declara en la hoja y no se
            hereda:** `grep -n 'code {' src/estilos.css` da TRES reglas —
            `.plantillas code`, `.simulador__registro code`, `.sim__ultimo-turno
            code`— y **ninguna regla de base para `code`**. El `<code>` de acá no
            cae en ninguna de las tres, así que `--fuente-dato` lo pone
            `.identificador-acortado__completo` en §«Patrones de R3». Sin esa
            regla, este nodo se vería con la fuente del documento y ningún examen
            de `pruebas/` lo diría: `happy-dom` no resuelve la cascada.
          */}
          <code className="identificador-acortado__completo">{identificador}</code>
          <button type="button" className="identificador-acortado__copiar" onClick={copiar}>
            Copiar
          </button>
        </span>
      )}
    </span>
  )
}
